import * as React from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useRouteContext } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import type { TagDefinition } from '@ooh/contracts';
import { tagsPickerQuery } from '../api/queries';
import { Badge } from '@ooh/ui/components/badge';
import { Button } from '@ooh/ui/components/button';
import { Popover, PopoverContent, PopoverTrigger } from '@ooh/ui/components/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@ooh/ui/components/command';

interface TagsSectionProps {
  tagDefinitionIds: number[];
  onChange: (ids: number[]) => void;
  /** Passed from parent — no internal useTranslation (packages/ui convention applied here too) */
  t: TFunction<'inventory'>;
}

export function TagsSection({ tagDefinitionIds, onChange, t }: TagsSectionProps) {
  const [open, setOpen] = React.useState(false);
  const { auth } = useRouteContext({ from: '__root__' });

  const { data: rawTags = [] } = useQuery<TagDefinition[]>(tagsPickerQuery(auth));
  // Guard against stale cache returning inactive rows (server already filters, but be defensive)
  const availableTags = rawTags.filter((tag) => tag.isActive);

  // Build a map id→label for chip display (includes any ids no longer in availableTags)
  const tagById = React.useMemo<Map<number, TagDefinition>>(
    () => new Map(availableTags.map((t) => [t.id, t])),
    [availableTags],
  );

  const handleToggle = React.useCallback(
    (id: number) => {
      if (tagDefinitionIds.includes(id)) {
        onChange(tagDefinitionIds.filter((x) => x !== id));
      } else {
        onChange([...tagDefinitionIds, id]);
      }
    },
    [tagDefinitionIds, onChange],
  );

  const handleRemoveChip = React.useCallback(
    (id: number) => {
      onChange(tagDefinitionIds.filter((x) => x !== id));
    },
    [tagDefinitionIds, onChange],
  );

  const selectedCount = tagDefinitionIds.length;
  const triggerLabel =
    selectedCount > 0
      ? `${String(selectedCount)} ${t('form.tags.sectionTitle')}`
      : t('form.tags.placeholder');

  return (
    <div className="space-y-2" data-testid="tags-section">
      {/* Selected chips */}
      {tagDefinitionIds.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {tagDefinitionIds.map((id) => {
            const tag = tagById.get(id);
            const label = tag?.label ?? String(id);
            return (
              <Badge
                key={id}
                variant="outline"
                className="flex items-center gap-1 pr-1 text-xs"
                data-testid={`tag-chip-${id}`}
              >
                <span>{label}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('form.tags.removeAriaLabel', { label })}
                  className="ml-0.5 h-4 w-4 rounded-full p-0"
                  onClick={() => handleRemoveChip(id)}
                  data-testid={`tag-chip-remove-${id}`}
                >
                  <X className="h-2.5 w-2.5" />
                </Button>
              </Badge>
            );
          })}
        </div>
      )}

      {/* Picker trigger */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full justify-between text-sm font-normal"
            data-testid="tags-picker-trigger"
          >
            <span className="text-muted-foreground">{triggerLabel}</span>
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground ml-2 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="start">
          <Command>
            <CommandInput placeholder={t('form.tags.searchPlaceholder')} />
            <CommandList>
              <CommandEmpty>{t('form.tags.noResults')}</CommandEmpty>
              <CommandGroup>
                {availableTags.map((tag) => {
                  const isSelected = tagDefinitionIds.includes(tag.id);
                  return (
                    <CommandItem
                      key={tag.id}
                      value={tag.label}
                      onSelect={() => handleToggle(tag.id)}
                      data-testid={`tag-item-${tag.id}`}
                    >
                      <Check
                        className={`h-3.5 w-3.5 mr-2 shrink-0 ${isSelected ? 'opacity-100' : 'opacity-0'}`}
                      />
                      {tag.label}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
